/**
 * ExportarConferenciaMenu — botão "Exportar" do cabeçalho da Conferência de Entrega.
 *
 * Menu com "Exportar HTML (.html)" e "Exportar Markdown (.md)". Desabilitado (com o
 * motivo no `title` e lido pelo leitor de tela) enquanto não há conferência gerada.
 * Teclado: Enter/Espaço abre, ↑/↓/Home/End navegam, Escape fecha e devolve o foco.
 */
import { useEffect, useId } from 'react';
import { Download, FileCode2, FileText } from 'lucide-react';
import { C } from '@/theme/palette';
import { usePopover, moverFocoNoMenu } from '@/ui/sphere/usePopover';

const ITENS = [
  { formato: 'html', rotulo: 'Exportar HTML (.html)', dica: 'Mesmo visual da tela, abre offline', Icone: FileCode2 },
  { formato: 'md', rotulo: 'Exportar Markdown (.md)', dica: 'Todas as seções, em texto', Icone: FileText },
];

export function ExportarConferenciaMenu({ disabled = false, motivo = '', ocupado = false, onExportar }) {
  const { open, setOpen, close, triggerRef, panelRef } = usePopover();
  const idMenu = useId();
  const idMotivo = useId();
  const bloqueado = disabled || ocupado;

  // Ao abrir, o foco vai para o primeiro item.
  useEffect(() => {
    if (open) panelRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [open, panelRef]);

  function escolher(formato) {
    close(true);
    onExportar?.(formato);
  }

  return (
    <div data-export="omitir" style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        disabled={bloqueado}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? idMenu : undefined}
        aria-describedby={disabled && motivo ? idMotivo : undefined}
        title={disabled ? (motivo || undefined) : 'Exportar a Conferência completa'}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); }
        }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          cursor: bloqueado ? 'default' : 'pointer',
          background: 'transparent', color: C.text, border: `1px solid ${C.border}`,
          borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
          opacity: bloqueado ? 0.55 : 1,
        }}
      >
        <Download size={15} aria-hidden="true" />
        {ocupado ? 'Exportando…' : 'Exportar'}
      </button>
      {disabled && motivo && (
        <span id={idMotivo} className="sr-only">{motivo}</span>
      )}
      {open && (
        <div
          ref={panelRef}
          id={idMenu}
          role="menu"
          aria-label="Exportar a Conferência de Entrega"
          className="sp-popover"
          onKeyDown={(e) => {
            if (e.key === 'Tab') { close(false); return; }
            moverFocoNoMenu(e, panelRef.current);
          }}
          style={{ right: 0, minWidth: 260 }}
        >
          {ITENS.map(({ formato, rotulo, dica, Icone }) => (
            <button
              key={formato}
              type="button"
              role="menuitem"
              className="sp-popover-item"
              onClick={() => escolher(formato)}
            >
              <Icone size={16} aria-hidden="true" style={{ color: C.cyber, flexShrink: 0 }} />
              <span className="sp-popover-text">
                <span>{rotulo}</span>
                <small>{dica}</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ExportarConferenciaMenu;
